import { baseApi } from "../../../app/api";
import type { ThemeVariables } from "../../../types";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

// Import Types for this API slice
import type {
  TrCityWithDistricts,
  TrCity,
  PlaceOrderParams,
  RawPlaceOrderResponse,
  TransformedPlaceOrderResult,
} from "../types/checkoutApiTypes";

// Import helper functions from cartApi
import {
  transformAddressToRaw,
  transformAddress,
} from "../../cart/api/cartApi";

// Import TransformedCart for Iyzico payload
import type { TransformedCart } from "../../cart/types/cartApiTypes";

// --- Iyzico-specific Types ---
export interface InitiateIyzicoPaymentParams {
  cart: TransformedCart;
}

export interface InitiateIyzicoPaymentResult {
  status: "success";
  token: string;
  paymentPageUrl: string;
  orderId: number;
  orderKey: string;
}
// --- End Iyzico-specific Types ---

// --- Base URL Constants ---
const WC_STORE_API_BASE =
  window.themeVariables?.wcStoreApiBase || "/wp-json/wc/store/v1";

const IYZICO_API_BASE =
  window.themeVariables?.rootUrl + "birikiuc-iyzico/v1" ||
  "/wp-json/birikiuc-iyzico/v1";

// --- Helper Functions ---

// Helper for loading static JSON data (cities, districts)
const getStaticDataPath = (): string => {
  const themeVars = window.themeVariables as ThemeVariables | undefined;
  const themePath = themeVars?.themePath || "";
  const isDevelopment = import.meta.env.DEV;
  const devDataPathBase = `${themePath}/assets/src/data`;
  const prodDataPathBase = `${themePath}/assets/dist/assets/data`;
  return isDevelopment ? devDataPathBase : prodDataPathBase;
};

// Transformation for standard WC /checkout response (used by BACS/QNB)
export function transformPlaceOrderResponse(
  response: RawPlaceOrderResponse
): TransformedPlaceOrderResult {
  return {
    orderId: response.order_id,
    status: response.status,
    orderKey: response.order_key,
    customerNote: response.customer_note,
    customerId: response.customer_id,
    billingAddress: transformAddress(response.billing_address),
    shippingAddress: transformAddress(response.shipping_address),
    paymentMethod: response.payment_method,
    paymentResult: response.payment_result
      ? {
          paymentStatus: response.payment_result.payment_status,
          paymentDetails: response.payment_result.payment_details || [],
          redirectUrl: response.payment_result.redirect_url || "",
        }
      : { paymentStatus: "error", paymentDetails: [], redirectUrl: "" },
  };
}

// --- API Slice Definition ---
export const checkoutApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // --- Static Data Endpoints ---
    getCities: builder.query<TrCity[], void>({
      async queryFn(_arg, _queryApi, _extraOptions) {
        const dataPathBase = getStaticDataPath();
        try {
          const response = await fetch(`${dataPathBase}/tr_cities.json`);
          if (!response.ok)
            throw new Error(`Failed to fetch cities: ${response.statusText}`);
          const data: TrCity[] = await response.json();
          return { data };
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to load city data";
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: errorMessage,
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: ["Cities"],
    }),

    getCitiesWithDistricts: builder.query<TrCityWithDistricts[], void>({
      async queryFn(_arg, _queryApi, _extraOptions) {
        const dataPathBase = getStaticDataPath();
        try {
          const response = await fetch(
            `${dataPathBase}/tr_cities_districts.json`
          );
          if (!response.ok)
            throw new Error(
              `Failed to fetch districts: ${response.statusText}`
            );
          const data: TrCityWithDistricts[] = await response.json();
          return { data };
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to load district data";
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: errorMessage,
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: ["CitiesWithDistricts"],
    }),

    // --- Standard WooCommerce Place Order Mutation (for BACS/QNB) ---
    placeOrder: builder.mutation<TransformedPlaceOrderResult, PlaceOrderParams>(
      {
        query: (params) => {
          const body: Record<string, any> = {
            billing_address: transformAddressToRaw(params.billingAddress),
            shipping_address: transformAddressToRaw(params.shippingAddress),
            payment_method: params.paymentMethod,
            ...(params.customerNote && { customer_note: params.customerNote }),
          };
          return {
            url: `${WC_STORE_API_BASE}/checkout`,
            method: "POST",
            body: body,
          };
        },
        transformResponse: transformPlaceOrderResponse,
        invalidatesTags: ["Cart"],
      }
    ),

    // --- Initiate Iyzico Payment Mutation ---
    initiateIyzicoPayment: builder.mutation<
      InitiateIyzicoPaymentResult,
      InitiateIyzicoPaymentParams
    >({
      query: (params) => {
        // Our custom endpoint expects a single `cart` object in the body,
        // which contains the full cart state including addresses.
        const body = {
          cart: {
            ...params.cart,
            billingAddress: params.cart.billingAddress,
            shippingAddress: params.cart.shippingAddress,
          },
        };
        return {
          url: `${IYZICO_API_BASE}/iyzico/initiate-payment`,
          method: "POST",
          body,
        };
      },
      // This mutation creates a draft order but doesn't finalize it,
      // so it shouldn't invalidate the 'Cart' tag yet. The callback will handle that.
    }),
  }),
  overrideExisting: false,
});

// --- Export Hooks and Endpoints ---
export const {
  useGetCitiesQuery,
  useGetCitiesWithDistrictsQuery,
  usePlaceOrderMutation,
  useInitiateIyzicoPaymentMutation,
} = checkoutApi;

export const {
  getCities,
  getCitiesWithDistricts,
  placeOrder,
  initiateIyzicoPayment,
} = checkoutApi.endpoints;
